import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { DESTINATIONS } from "@/data/content";
import { scrollToSection } from "@/lib/scroll";

export default function Destinations() {
  return (
    <section id="destinations" data-testid="destinations-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-[#d4af37] font-medium mb-6">
              Where We Take You
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-tight text-slate-50 max-w-xl">
              Eleven destinations.{" "}
              <span className="italic text-[#d4af37]">One standard.</span>
            </h2>
          </div>
          <p className="text-slate-400 max-w-sm leading-relaxed">
            Each journey is drawn by hand — escorted tours or free-spirited
            routes, always at your pace.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-5">
          {DESTINATIONS.map((d, i) => (
            <motion.button
              key={d.name}
              data-testid={`destination-card-${d.name.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => scrollToSection("#booking")}
              className={`group relative overflow-hidden h-80 text-left ${d.span}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
            >
              <img
                src={d.image}
                alt={`${d.name} — ${d.tagline}`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/95 via-[#0A1128]/25 to-transparent" />
              <div className="absolute inset-0 border border-[#d4af37]/0 group-hover:border-[#d4af37]/50 transition-colors duration-500" />
              <div className="absolute bottom-0 inset-x-0 p-6 flex items-end justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37] mb-2">
                    {d.tagline}
                  </p>
                  <h3 className="font-display text-3xl font-light text-slate-50">
                    {d.name}
                  </h3>
                </div>
                <ArrowUpRight
                  size={22}
                  className="text-[#d4af37] opacity-0 -translate-x-2 translate-y-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-500"
                />
              </div>
            </motion.button>
          ))}
          <motion.button
            data-testid="destination-card-your-journey"
            onClick={() => scrollToSection("#booking")}
            className="group relative overflow-hidden h-80 lg:col-span-2 border border-[#d4af37]/30 bg-[#101C3E]/60 flex flex-col items-center justify-center gap-4 text-center px-6 hover:bg-[#d4af37] transition-colors duration-500"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="font-display text-3xl font-light italic text-[#d4af37] group-hover:text-[#0A1128] transition-colors duration-500">
              Your journey, next.
            </span>
            <span className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-slate-300 group-hover:text-[#0A1128] transition-colors duration-500">
              Request a route <ArrowUpRight size={14} />
            </span>
          </motion.button>
        </div>
      </div>
    </section>
  );
}
